import { Document, Font, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import DesignConstants from "../../../constants/DesignConstants";
import DownloadCodesPDFCard from "./DownloadCodesPDFCard";
import type { DownloadCodesClassMemberWithQR } from "../../../utils/qrCodeUtils";

export interface DownloadCodesPDFTranslations {
    classCode: string;
    personalCode: string;
    classroom: string;
    accessCodes: string;
    emptyStateTitle: string;
    emptyStateDescription: string;
}

interface DownloadCodesPDFDocumentProps {
    classMembers: DownloadCodesClassMemberWithQR[];
    schoolClassId: string;
    schoolClassName: string;
    translations: DownloadCodesPDFTranslations;
}

const COLUMNS = 2;

const isLocalDev = process.env.NODE_ENV === "development";

Font.registerHyphenationCallback((word: string) => [word]);

Font.register({
    family: DesignConstants.PDF.PROXIMA_FONT,
    fonts: [
        {
            src: isLocalDev
                ? "/fonts/proximanova-semibold.ttf"
                : "https://www.phase-6.de/system/galleries/html/fonts/proximanova-semibold.ttf",
            fontWeight: 600,
        },
        {
            src: isLocalDev
                ? "/fonts/proximanova-regular.ttf"
                : "https://www.phase-6.de/system/galleries/html/fonts/proximanova-regular.ttf",
            fontWeight: 400,
        },
    ],
});

const DownloadCodesPDFDocument = (props: DownloadCodesPDFDocumentProps) => {
    const rows: DownloadCodesClassMemberWithQR[][] = [];
    for (let i = 0; i < props.classMembers.length; i += COLUMNS) {
        rows.push(props.classMembers.slice(i, i + COLUMNS));
    }

    const hasClassMembers = props.classMembers.length > 0;

    return (
        <Document title={`${props.schoolClassName} - ${props.translations.accessCodes}`}>
            <Page
                size="A4"
                style={styles.page}
                wrap
            >
                {hasClassMembers ? (
                    rows.map((rowClassMembers, rowIndex) => (
                        <View
                            key={rowIndex}
                            style={styles.row}
                            wrap={false}
                        >
                            {rowClassMembers.map((classMember, colIndex) => (
                                <DownloadCodesPDFCard
                                    key={colIndex}
                                    classMember={classMember}
                                    schoolClassId={props.schoolClassId}
                                    translations={props.translations}
                                    isLastColumn={colIndex === COLUMNS - 1}
                                    isLastRow={rowIndex === rows.length - 1}
                                />
                            ))}
                        </View>
                    ))
                ) : (
                    <View style={styles.emptyState}>
                        <Text style={styles.emptyStateTitle}>{props.translations.emptyStateTitle}</Text>
                        <Text style={styles.emptyStateDescription}>{props.translations.emptyStateDescription}</Text>
                    </View>
                )}
            </Page>
        </Document>
    );
};

const styles = StyleSheet.create({
    page: {
        flexDirection: "column",
        backgroundColor: DesignConstants.COLORS.WHITE,
        fontFamily: DesignConstants.PDF.PROXIMA_FONT,
        fontWeight: DesignConstants.PDF.REGULAR,
        paddingHorizontal: DesignConstants.PDF.GAP_M,
        paddingVertical: DesignConstants.PDF.GAP_M,
    },
    row: {
        flexDirection: "row",
    },
    emptyState: {
        alignItems: "center",
        padding: 60,
        gap: DesignConstants.PDF.GAP_S,
    },
    emptyStateTitle: {
        fontSize: DesignConstants.PDF.FONTSIZE_REGULAR,
        color: DesignConstants.COLORS.DARK_GREY,
    },
    emptyStateDescription: {
        fontSize: DesignConstants.PDF.FONTSIZE_REGULAR,
        color: DesignConstants.COLORS.DARK_GREY,
        textAlign: "center",
    },
});

export default DownloadCodesPDFDocument;
